import { Page, usePage } from "@tree-company/tree-query";
import translate from "../locales";
import { useContext, useEffect } from "react";
import clsx from "clsx";
import { Header } from "./header";
import { calculateResult, constructRedirectURL } from "../helpers";
import { LanguageContext } from "../app";
import { SurveyContext } from "../app";
import logoCREG from "../assets/images/sources/creg.png";
import logoCWAPE from "../assets/images/sources/cwape.png";
import logoVREG from "../assets/images/sources/vreg.png";
import logoOmbuds from "../assets/images/sources/ombuds.png";
import logoGreenpeace from "../assets/images/sources/greenpeace.png";

const MIN_WAIT_TIME = 6000;

const sources = [
  {
    name: "Creg",
    logo: logoCREG,
  },
  {
    name: "vreg",
    logo: logoVREG,
  },
  {
    name: "cwape",
    logo: logoCWAPE,
  },
  {
    name: "Ombuds",
    logo: logoOmbuds,
  },
  {
    name: "Greenpeace",
    logo: logoGreenpeace,
  },
];

const parameters = [
  "price",
  "origin",
  "costumer_satisfaction",
  "source",
  "invests_in_renewable",
  "stable_price",
  "adjusted_formula",
];

export function Result({ page }: { page: Page }) {
  const language = useContext(LanguageContext);
  const survey = useContext(SurveyContext);
  const treesponse = page.treesponse;
  const { htmlAttributes, title } = usePage(page);

  const result = calculateResult(treesponse);
  const resultURL = constructRedirectURL(language, result);

  async function storeResultAndRedirect() {
    const startTime = performance.now();

    for (const ranking of result.rankings) {
      treesponse.results[ranking.name] = ranking.percentage;
    }
    treesponse.completed = true;

    const promises: Promise<void | Response>[] = [treesponse.save()];
    if (treesponse.getAnswer("mailResults") && treesponse.email) {
      promises.push(
        fetch(`/.netlify/functions/send_profile`, {
          method: "POST",
          body: JSON.stringify({
            url: resultURL,
            email: treesponse.email,
            language,
          }),
        }),
      );
    }
    await Promise.allSettled(promises);

    if (result.totalWeight === 0) {
      survey.nextPage();
      return;
    }

    const endTime = performance.now();

    setTimeout(
      () => {
        window.location.href = resultURL;
      },
      Math.max(0, MIN_WAIT_TIME + startTime - endTime),
    );
  }

  // When this component mounts, we calculate and assign the result
  // Then redirect to the result page
  useEffect(() => {
    storeResultAndRedirect();
  }, []);

  return (
    <div className={clsx("page", htmlAttributes.class)}>
      <Header page={page} />
      <div className="page__main-content">
        {title && <h1 className="page__title page__title--small">{title}</h1>}
        <h2 className="page__subtitle">
          {translate("result.subtitle", {}, language)}
        </h2>
        <ul className="parameters">
          {parameters.map((key) => (
            <li className="parameters__item" key={key}>
              {translate(["result", "parameters", key], {}, language)}
            </li>
          ))}
        </ul>

        <div className="sources">
          <h2 className="page__subtitle sources__title">
            {translate("result.sources_title", {}, language)}
          </h2>
          <div className="source-banner">
            <div className="source-banner__items">
              {sources.map((s) => (
                <img
                  src={s.logo}
                  alt={s.name}
                  key={s.name}
                  className={clsx(
                    "source-banner__item",
                    `source-banner__item--${s.name}`,
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
